import type { Channel } from "@skyware-group/iris-api-model";

import { BaseClient } from "../../clients/base/BaseClient";
import type { Paged, PaginationParams } from "../_shared/pagination.types";
import { CHANNELS_ENDPOINTS } from "./channels.consts";
import type { UpdateChannelStatusParams, ChannelCreateRequest, ChannelUpdateRequest } from "./channels.types";

/**
 * Channels API Client
 * Handles all channel-related API operations.
 */
export class ChannelsClient extends BaseClient {
  /**
   * Fetch a page of channels. The home page walks all pages to build the
   * location-grouped view (see `collectAllPages`).
   * @param {PaginationParams} params - Optional pagination parameters.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<Paged<Channel>>} One page of channels plus paging metadata.
   */
  async fetchChannels(params?: PaginationParams, signal?: AbortSignal | null): Promise<Paged<Channel>> {
    return this.get<Paged<Channel>>(CHANNELS_ENDPOINTS.CHANNELS, {
      params: params as Record<string, unknown>,
      signal,
    });
  }

  /**
   * Fetch a specific channel by ID.
   * @param {number} id - Channel ID.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<Channel>} Channel details.
   */
  async fetchChannelById(id: number, signal?: AbortSignal | null): Promise<Channel> {
    return this.get<Channel>(CHANNELS_ENDPOINTS.CHANNEL_BY_ID(id), {
      signal,
    });
  }

  /**
   * Fetch all currently running channels.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<Channel[]>} Array of running channels.
   */
  async fetchRunningChannels(signal?: AbortSignal | null): Promise<Channel[]> {
    return this.get<Channel[]>(CHANNELS_ENDPOINTS.RUNNING_CHANNELS, {
      signal,
    });
  }

  /**
   * Fetch pending items count for a specific channel.
   * @param {number} id - Channel ID.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<number>} Number of pending items.
   */
  async fetchChannelPendingCount(id: number, signal?: AbortSignal | null): Promise<number> {
    return this.get<number>(CHANNELS_ENDPOINTS.CHANNEL_PENDING(id), {
      signal,
    });
  }

  /**
   * Start or stop a channel.
   * @param {number} id - Channel ID.
   * @param {UpdateChannelStatusParams} params - Status update parameters.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<Channel>} Updated channel details.
   */
  async updateChannelStatus(
    id: number,
    params: UpdateChannelStatusParams,
    signal?: AbortSignal | null,
  ): Promise<Channel> {
    return this.put<Channel>(CHANNELS_ENDPOINTS.CHANNEL_STATUS(id), {
      params: params as unknown as Record<string, unknown>,
      signal,
    });
  }

  /**
   * Start a channel (convenience method).
   * @param {number} id - Channel ID.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<Channel>} Updated channel details.
   */
  async startChannel(id: number, signal?: AbortSignal | null): Promise<Channel> {
    return this.updateChannelStatus(id, { newStatus: true }, signal);
  }

  /**
   * Stop a channel (convenience method).
   * @param {number} id - Channel ID.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<Channel>} Updated channel details.
   */
  async stopChannel(id: number, signal?: AbortSignal | null): Promise<Channel> {
    return this.updateChannelStatus(id, { newStatus: false }, signal);
  }

  /**
   * Create a new channel. Admin only.
   */
  async createChannel(data: ChannelCreateRequest, signal?: AbortSignal | null): Promise<Channel> {
    return this.post<Channel>(CHANNELS_ENDPOINTS.CREATE_CHANNEL, { body: data, signal });
  }

  /**
   * Update an existing channel. Admin only.
   */
  async updateChannel(id: number, data: ChannelUpdateRequest, signal?: AbortSignal | null): Promise<Channel> {
    return this.put<Channel>(CHANNELS_ENDPOINTS.UPDATE_CHANNEL(id), { body: data, signal });
  }

  /**
   * Delete a channel. Admin only.
   */
  async deleteChannel(id: number, signal?: AbortSignal | null): Promise<void> {
    return this.delete<never>(CHANNELS_ENDPOINTS.DELETE_CHANNEL(id), { signal });
  }
}
